import { h } from './index-6e9159ae.js';
import { A as ArrowBottomIcon } from './arrow-bottom-rounded-043572da.js';
import { A as ArrowUp, C as Checkbox } from './checkbox-e2f53258.js';
import { C as CloseIcon } from './close-ff816971.js';
import { B as Button } from './button-ab6bd1ba.js';
import { H as Heading } from './heading-11a0d09a.js';
import { g as getRandomArbitrary, r as randomID } from './utils-71996adb.js';
import { c as createRipple } from './ripple-d1912f02.js';
import { r as regexEncode } from './string-utils-d9206c15.js';
import { F as FieldsetGroup } from './fieldset-group-378f39ad.js';

const FacetContainer = (_, children) => (h("div", { class: "bg-background border border-neutral rounded-lg p-4", part: "facet" }, children));

const FacetHeader = (props) => {
  const label = props.i18n.t(props.label);
  const expandFacet = props.i18n.t('expand-facet', { label });
  const collapseFacet = props.i18n.t('collapse-facet', { label });
  const clearFilters = props.i18n.t('clear-filters', {
    count: props.numberOfSelectedValues,
  });
  const clearFiltersForFacet = props.i18n.t('clear-filters-for-facet', {
    count: props.numberOfSelectedValues,
    label,
  });
  return [
    h(Button, { style: "text-transparent", part: "label-button", class: "flex font-bold justify-between w-full py-1 px-2 text-lg rounded-none", title: props.isCollapsed ? expandFacet : collapseFacet, onClick: () => props.onToggleCollapse(), ariaExpanded: (!props.isCollapsed).toString(), ref: props.headerRef },
      h(Heading, { level: props.headingLevel, class: "truncate" }, label),
      h("atomic-icon", { part: "label-button-icon", class: "w-3 self-center shrink-0 ml-4", icon: props.isCollapsed ? ArrowBottomIcon : ArrowUp })),
    props.onClearFilters && props.numberOfSelectedValues > 0 && (h(Button, { style: "text-primary", part: "clear-button", class: "flex items-baseline max-w-full p-2 text-sm", ariaLabel: clearFiltersForFacet, onClick: () => props.onClearFilters() },
      h("atomic-icon", { part: "clear-button-icon", class: "w-2 h-2 mr-1", icon: CloseIcon }),
      h("span", null, clearFilters))),
  ];
};

const FacetPlaceholder = ({ numberOfValues, isCollapsed, }) => {
  const facetValues = [];
  for (let i = 0; i < numberOfValues; i++) {
    const width = `${getRandomArbitrary(60, 100)}%`;
    const opacity = `${getRandomArbitrary(0.3, 1)}`;
    facetValues.push(h("div", { class: "flex bg-neutral h-5 mt-4", style: { width, opacity } }));
  }
  return (h("div", { part: "placeholder", class: "bg-background animate-pulse border border-neutral rounded-lg mb-4 p-7", "aria-hidden": "true" },
    h("div", { class: "bg-neutral rounded h-8", style: { width: `${getRandomArbitrary(25, 75)}%` } }),
    !isCollapsed && h("div", { class: "mt-7" }, facetValues)));
};

/*!
 * escape-html
 * Copyright(c) 2012-2013 TJ Holowaychuk
 * Copyright(c) 2015 Andreas Lubbe
 * Copyright(c) 2015 Tiancheng "Timothy" Gu
 * MIT Licensed
 */

/**
 * Module variables.
 * @private
 */

var matchHtmlRegExp = /["'&<>]/;

/**
 * Module exports.
 * @public
 */

var escapeHtml_1 = escapeHtml;

/**
 * Escape special characters in the given string of html.
 *
 * @param  {string} string The string to escape for inserting into HTML
 * @return {string}
 * @public
 */

function escapeHtml(string) {
  var str = '' + string;
  var match = matchHtmlRegExp.exec(str);

  if (!match) {
    return str;
  }

  var escape;
  var html = '';
  var index = 0;
  var lastIndex = 0;

  for (index = match.index; index < str.length; index++) {
    switch (str.charCodeAt(index)) {
      case 34: // "
        escape = '&quot;';
        break;
      case 38: // &
        escape = '&amp;';
        break;
      case 39: // '
        escape = '&#39;';
        break;
      case 60: // <
        escape = '&lt;';
        break;
      case 62: // >
        escape = '&gt;';
        break;
      default:
        continue;
    }

    if (lastIndex !== index) {
      html += str.substring(lastIndex, index);
    }

    lastIndex = index + 1;
    html += escape;
  }

  return lastIndex !== index
    ? html + str.substring(lastIndex, index)
    : html;
}

/**
 * Meant to be used inside the `componentShouldUpdate` lifecycle method.
 * It prevents updating the facet between two matchless facet searches.
 * It also prevents updating the facet until the first search has loaded.
 */
function shouldUpdateFacetSearchComponent(nextFacetSearchState, prevFacetSearchState) {
  const hasQuery = nextFacetSearchState.query !== '';
  const stillNoValues = !nextFacetSearchState.values.length && !prevFacetSearchState.values.length;
  const hasFinishedLoading = !nextFacetSearchState.isLoading && prevFacetSearchState.isLoading;
  if (hasQuery && stillNoValues) {
    return hasFinishedLoading;
  }
  return true;
}
function shouldDisplaySearchResults(facetSearchState) {
  const hasQuery = facetSearchState.query !== '';
  const isLoading = facetSearchState.isLoading;
  const hasValues = !!facetSearchState.values.length;
  if (!hasQuery) {
    return false;
  }
  if (hasValues) {
    return true;
  }
  return !isLoading;
}
function highlightSearchResult(resultValue, searchQuery = '') {
  const sanitizedResult = escapeHtml_1(resultValue);
  if (searchQuery.trim() === '') {
    return sanitizedResult;
  }
  const regex = new RegExp(`(${regexEncode(searchQuery)})`, 'i');
  return escapeHtml_1(resultValue).replace(regex, '<span part="search-highlight" class="font-bold">$1</span>');
}

const FacetValueCheckbox = (props, children) => {
  const id = randomID('facet-value-');
  const count = props.numberOfResults.toLocaleString(props.i18n.language);
  const ariaLabel = props.i18n.t('facet-value', {
    value: props.displayValue,
    count: props.numberOfResults,
    interpolation: { escapeValue: false },
  });
  let labelRef;
  return (h("li", { key: props.displayValue, class: "relative flex items-center" },
    h(Checkbox, { id: id, checked: props.isSelected, onToggle: () => props.onClick(), part: "value-checkbox", class: "value-checkbox", ariaLabel: ariaLabel, ref: props.buttonRef, onMouseDown: (e) => createRipple(e, { color: 'neutral', parent: labelRef }), iconPart: "value-checkbox-icon" }),
    h("label", { ref: (ref) => (labelRef = ref), htmlFor: id, part: "value-checkbox-label", class: "group items-center", onMouseDown: (e) => createRipple(e, { color: 'neutral' }), "aria-hidden": "true" },
      children,
      h("span", { part: "value-count", class: "value-count" }, props.i18n.t('between-parentheses', {
        text: count,
      })))));
};

const FacetValueLabelHighlight = (props) => {
  return (
  // deepcode ignore ReactSetInnerHtml: This is not React code
  h("span", { title: props.displayValue, part: "value-label", class: `value-label truncate group-hover:text-primary group-focus:text-primary ${props.isSelected ? 'font-bold' : ''}`, innerHTML: highlightSearchResult(props.displayValue, props.searchQuery) }));
};

const FacetValuesGroup = (props, children) => {
  if (!props.label) {
    return children;
  }
  const facetDisplayLabel = props.i18n.t(props.label);
  const groupLabel = props.query === undefined
    ? props.i18n.t('facet-values', { label: facetDisplayLabel })
    : props.i18n.t('facet-search-results', {
      query: props.query,
      label: facetDisplayLabel,
    });
  return h(FieldsetGroup, { label: groupLabel }, children);
};

export { FacetValueLabelHighlight as F, shouldDisplaySearchResults as a, FacetValuesGroup as b, FacetContainer as c, FacetHeader as d, FacetPlaceholder as e, escapeHtml_1 as f, FacetValueCheckbox as g, shouldUpdateFacetSearchComponent as s };

//# sourceMappingURL=facet-values-group-e87e165b.js.map